/**
 * WalletConnect session → vault bindings across tabs (review D-12).
 *
 * Bindings live in this tab's memory and in localStorage, which every tab of
 * the app shares (as does the WalletConnect session store). Another tab can
 * pair a dApp, and persist its binding, while this tab's WalletKit is still
 * initialising, so decisions about which sessions are unbound must use the
 * stored bindings as they are at that moment, not the copy this tab loaded
 * at mount.
 *
 * Pure.
 */

function hasTopic(bindings: Record<string, unknown>, topic: string): boolean {
  return Object.prototype.hasOwnProperty.call(bindings, topic);
}

/**
 * Active sessions with no vault binding, neither in this tab's memory nor in
 * storage as just re-read: these are disconnected (e.g. left over from a
 * previous user on this browser, whose bindings were cleared at logout).
 */
export function findOrphanTopics(
  activeTopics: readonly string[],
  inMemory: Record<string, unknown>,
  stored: Record<string, unknown>
): string[] {
  return activeTopics.filter((topic) => !hasTopic(inMemory, topic) && !hasTopic(stored, topic));
}

/**
 * This tab's bindings plus the stored bindings of active sessions it does not
 * know yet (paired in another tab), or null when there is nothing to adopt.
 */
export function adoptStoredBindings<T>(
  activeTopics: readonly string[],
  inMemory: Record<string, T>,
  stored: Record<string, T>
): Record<string, T> | null {
  const adopted = activeTopics.filter(
    (topic) => !hasTopic(inMemory, topic) && hasTopic(stored, topic)
  );
  if (adopted.length === 0) return null;
  const next: Record<string, T> = { ...inMemory };
  for (const topic of adopted) {
    const binding = stored[topic];
    if (binding !== undefined) next[topic] = binding;
  }
  return next;
}
